// src/Components/Admin/KmlDataView.js

import React from 'react';
import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import Paper from '@mui/material/Paper';
import Table from '@mui/material/Table';
import TableBody from '@mui/material/TableBody';
import TableCell from '@mui/material/TableCell';
import TableContainer from '@mui/material/TableContainer';
import TableHead from '@mui/material/TableHead';
import TableRow from '@mui/material/TableRow';
import Checkbox from '@mui/material/Checkbox';

const cellStyle = { paddingLeft: '8px', paddingRight: '8px' };

export default function KmlDataView({
  parsedData,
  selectedImportIds,
  setSelectedImportIds,
  excelHasData,
  makeUppercase,
}) {
  const hasData = parsedData && parsedData.length > 0;

  const unimportedRows = parsedData ? parsedData.filter((r) => !r.isImported) : [];

  const showColorCol = parsedData ? parsedData.some((r) => !!r.color) : false;
  const showRichTextCol = parsedData ? parsedData.some((r) => !!r.hasRichText) : false;

  // Calculate percentage width to keep data columns perfectly equal
  const dataColCount = 1 + (showColorCol ? 1 : 0) + (showRichTextCol ? 1 : 0);
  const colWidth = `${100 / dataColCount}%`;

  const handleCheckboxChange = (id) => {
    setSelectedImportIds((prev) => {
      if (prev.includes(id)) {
        return prev.filter((item) => item !== id);
      } else {
        return [...prev, id];
      }
    });
  };

  const handleSelectAll = (checked) => {
    if (checked) {
      setSelectedImportIds(unimportedRows.map((row) => row.id));
    } else {
      setSelectedImportIds([]);
    }
  };

  const rowCount = unimportedRows.length;
  const numSelected = selectedImportIds.length;
  const isAllSelected = rowCount > 0 && numSelected === rowCount;
  const isIndeterminate = numSelected > 0 && numSelected < rowCount;

  return (
    <TableContainer
      component={Paper}
      elevation={0}
      sx={{
        flexGrow: 1,
        width: '100%',
        boxSizing: 'border-box',
        borderTop: hasData ? '2px solid #2e7d32' : 'none',
        borderBottom: 'none',
        borderLeft: 'none',
        borderRight: 'none',
        borderRadius: 0,
        overflowY: 'auto',
        overflowX: 'hidden',
        scrollBehavior: 'smooth',
      }}>
      {!hasData ? (
        <Box
          sx={{
            display: 'flex',
            flexDirection: 'column',
            justifyContent: 'center',
            alignItems: 'center',
            height: '100%',
            p: 3,
            textAlign: 'center',
            gap: 2,
          }}>
          <Typography color='error' variant='body2' sx={{ fontWeight: 'bold' }}>
            WARNING: Exercise caution in uploading the same data multiple times. Check
            first by uploading only one or two data points.
          </Typography>
          <Typography color='text.secondary' variant='body2' sx={{ fontStyle: 'italic' }}>
            {excelHasData
              ? 'Please clear the data in Batch Import before uploading a KML/KMZ file.'
              : 'Click the Upload button to select a .kml / .kmz file to upload.'}
          </Typography>
        </Box>
      ) : (
        <Table stickyHeader size='small' sx={{ width: '100%', tableLayout: 'auto' }}>
          <TableHead>
            <TableRow
              sx={{
                height: '28px',
                '& th': {
                  backgroundColor: '#e8f5e9',
                  fontWeight: 'bold',
                  borderBottom: '2px solid #2e7d32',
                  py: '2px',
                  ...cellStyle,
                },
              }}>
              <TableCell
                padding='checkbox'
                sx={{
                  width: '64px',
                  minWidth: '64px',
                  maxWidth: '64px',
                  textAlign: 'center',
                }}>
                <Checkbox
                  color='primary'
                  indeterminate={isIndeterminate}
                  checked={isAllSelected}
                  onChange={(e) => handleSelectAll(e.target.checked)}
                  size='small'
                  disabled={rowCount === 0}
                  sx={{ p: 0.5, '&.Mui-checked': { color: '#2e7d32' } }}
                />
              </TableCell>

              <TableCell align='left' sx={{ width: colWidth }}>
                Name
              </TableCell>

              {showColorCol && (
                <TableCell align='center' sx={{ width: colWidth }}>
                  Color
                </TableCell>
              )}
              {showRichTextCol && (
                <TableCell align='center' sx={{ width: colWidth }}>
                  Rich Text
                </TableCell>
              )}
            </TableRow>
          </TableHead>
          <TableBody>
            {parsedData.map((row) => {
              const isSelected = selectedImportIds.includes(row.id);
              const isImported = !!row.isImported;

              return (
                <TableRow
                  key={row.id}
                  hover={!isImported}
                  selected={isSelected}
                  onClick={() => !isImported && handleCheckboxChange(row.id)}
                  sx={{
                    height: '28px',
                    cursor: isImported ? 'default' : 'pointer',
                    opacity: isImported ? 0.5 : 1,
                    '& td': { py: '2px', ...cellStyle },
                    '&:last-child td, &:last-child th': { border: 0 },
                    '&.Mui-selected': { backgroundColor: 'transparent !important' },
                    '&.Mui-selected:hover': {
                      backgroundColor: 'rgba(0, 0, 0, 0.04) !important',
                    },
                  }}>
                  <TableCell
                    padding='checkbox'
                    sx={{
                      width: '64px',
                      minWidth: '64px',
                      maxWidth: '64px',
                      textAlign: 'center',
                    }}>
                    <Checkbox
                      checked={isImported ? true : isSelected}
                      disabled={isImported}
                      size='small'
                      sx={{
                        pointerEvents: 'none',
                        p: 0.5,
                        '&.Mui-checked': { color: isImported ? '#9e9e9e' : '#2e7d32' },
                        '&.Mui-disabled': { color: '#9e9e9e' },
                      }}
                    />
                  </TableCell>

                  <TableCell align='left'>
                    <Typography
                      variant='body2'
                      sx={{ color: 'text.primary', fontWeight: 'normal' }}>
                      {makeUppercase && typeof row.name === 'string'
                        ? row.name.toUpperCase()
                        : row.name}
                    </Typography>
                  </TableCell>

                  {showColorCol && (
                    <TableCell align='center'>
                      {row.color ? (
                        <Box
                          sx={{
                            display: 'inline-block',
                            width: 16,
                            height: 16,
                            borderRadius: '50%',
                            backgroundColor: row.color,
                            border: '1px solid rgba(0,0,0,0.2)',
                            verticalAlign: 'middle',
                          }}
                        />
                      ) : (
                        <Typography variant='caption' color='text.secondary'>
                          -
                        </Typography>
                      )}
                    </TableCell>
                  )}

                  {showRichTextCol && (
                    <TableCell align='center'>
                      {row.hasRichText ? (
                        <Box
                          sx={{
                            display: 'inline-block',
                            width: 14,
                            height: 14,
                            borderRadius: '50%',
                            backgroundColor: isImported ? '#9e9e9e' : '#00B0FF',
                            border: '1px solid rgba(0,0,0,0.15)',
                            verticalAlign: 'middle',
                          }}
                        />
                      ) : (
                        <Typography variant='caption' color='text.secondary'>
                          -
                        </Typography>
                      )}
                    </TableCell>
                  )}
                </TableRow>
              );
            })}
          </TableBody>
        </Table>
      )}
    </TableContainer>
  );
}
